import {
  Avatar,
  Button,
  FlexBox,
  Icon,
  InputText,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { ReadSearchApiArg } from 'lib-search-types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

import styles from './addFavoritesDropdown.module.scss';

type AddFavoritesDropdownProps = {
  onOpenChange?: (isOpen: boolean) => void;
};

export const AddFavoritesDropdown = ({
  onOpenChange,
}: AddFavoritesDropdownProps): JSX.Element => {
  const {
    isFavorite: checkIsFavorite,
    addFavorite,
    removeFavorite,
  } = useFavoriteAgents();

  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const hasOpenedRef = useRef(false);

  const [
    readSearchUsers,
    { results: agents, isFetching, isSuccess, hasNextPage, fetchNextPage },
  ] = useLazyReadSearchInfiniteQuery<'users'>();

  const searchArg = useMemo(
    (): ReadSearchApiArg<'users'> => ({
      scope: 'users',
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'user_type',
            operator: 'is_one',
            values: [{ value: 'member' }],
          },
        ],
      },
      sortBy: {
        field: 'display_name',
        order: 'asc',
      },
      pageSize: 50,
    }),
    [],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);

      if (open && !hasOpenedRef.current) {
        hasOpenedRef.current = true;
        readSearchUsers(searchArg);
      }

      if (!open) {
        setSearchQuery('');
      }

      onOpenChange?.(open);
    },
    [onOpenChange, readSearchUsers, searchArg],
  );

  const handleSearch = useCallback(
    (query: string) => {
      readSearchUsers({ ...searchArg, query }, { debounceTime: 300 });
    },
    [readSearchUsers, searchArg],
  );

  const toggleFavorite = useCallback(
    (agentId: string, isFavorite: boolean) => {
      if (isFavorite) {
        removeFavorite(agentId);
      } else {
        addFavorite(agentId);
      }
    },
    [addFavorite, removeFavorite],
  );

  return (
    <div className={styles.addFavoritesButton}>
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <Button variant="tertiary" icon="plus">
            Add favorites
          </Button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={styles.popoverContent}
            side="right"
            align="center"
          >
            <div className={styles.dropdownScroll}>
              <Text.Paragraph
                variant="3"
                color="tertiary"
                className={styles.dropdownTitle}
              >
                Add to favorites
              </Text.Paragraph>

              <div className={styles.searchInputWrapper}>
                <InputText
                  className={styles.searchInputText}
                  type="search"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleSearch(e.target.value);
                  }}
                  placeholder="Search agents"
                  suffix={
                    searchQuery
                      ? {
                          type: 'button',
                          button: {
                            icon: 'close',
                            onClick: () => {
                              setSearchQuery('');
                              handleSearch('');
                            },
                          },
                        }
                      : undefined
                  }
                />
              </div>

              <SelectionList isMultiple={false} selectedValues={[]}>
                {agents.map((agent) => {
                  const isFavorite = checkIsFavorite(agent.id);

                  return (
                    <SelectionList.Item
                      key={agent.id}
                      noFixedSelectionStateSpace
                      contentClassName={styles.itemContent}
                      onSelect={() => toggleFavorite(agent.id, isFavorite)}
                    >
                      <Avatar user={agent} size="24" />

                      <span className={styles.agentName}>{agent.name}</span>

                      <Icon
                        name={isFavorite ? 'star-full' : 'star'}
                        color={isFavorite ? 'orange.300' : 'neutral.400'}
                        size={20}
                        className={styles.favoriteIcon}
                      />
                    </SelectionList.Item>
                  );
                })}

                {isFetching && (
                  <li>
                    <FlexBox justifyContent="center" padding="200">
                      <Spinner size={16} />
                    </FlexBox>
                  </li>
                )}

                {isSuccess && agents.length === 0 && (
                  <li>
                    <Text.Paragraph
                      color="tertiary"
                      className={styles.emptyState}
                    >
                      No agents found
                    </Text.Paragraph>
                  </li>
                )}

                {!isFetching && hasNextPage && (
                  <li>
                    <Waypoint onEnter={fetchNextPage} />
                  </li>
                )}
              </SelectionList>
            </div>

            <Popover.Arrow
              style={{ fill: colors.neutral['000'] }}
              width={16}
              height={8}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
